import React, { useState, useEffect } from "react";
import { ChevronLeft, Eye, Sparkles, CheckCircle, XCircle, Send, Users, User, Wifi, WifiOff } from "lucide-react";
import { auth, db } from "../firebase-config";
import { doc, updateDoc, setDoc, getDoc, onSnapshot, serverTimestamp, arrayUnion } from "firebase/firestore";

export const PixelPuzzler = ({ onBack, clientObj, userObj, jukebox = [] }) => {
  const [gameMode, setGameMode] = useState(null); // 'solo' or 'hosted'
  const [modeSelected, setModeSelected] = useState(false);
  const [hostedGameData, setHostedGameData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [waitingForHost, setWaitingForHost] = useState(false);
  const [currentActiveTrack, setCurrentActiveTrack] = useState(null); // Track the active track index
  const [blurLevel, setBlurLevel] = useState(30); // Start with heavy blur/pixelation
  const [hints, setHints] = useState(0);
  const [currentArtist, setCurrentArtist] = useState(null);
  const [score, setScore] = useState(0);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongLetters, setWrongLetters] = useState([]);
  const [gameWon, setGameWon] = useState(false);
  const [currentInput, setCurrentInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [recentCorrect, setRecentCorrect] = useState([]);
  const [recentWrong, setRecentWrong] = useState([]);

  // Demo pop artists data
  const popArtists = [
    {
      id: "artist1",
      name: "TAYLOR SWIFT",
      imageUrl: "https://images.unsplash.com/photo-1544883892-62e2d4b1c3c8?w=400&h=400&fit=crop",
      hints: ["American singer-songwriter", "Known for 'Shake It Off'", "Previously country music artist"],
    },
    {
      id: "artist2",
      name: "ARIANA GRANDE",
      imageUrl: "https://images.unsplash.com/photo-1569230516719-d0c48621e2f1?w=400&h=400&fit=crop",
      hints: ["Pop and R&B singer", "Former Nickelodeon star", "Known for high ponytail"],
    },
    {
      id: "artist3",
      name: "BILLIE EILISH",
      imageUrl: "https://images.unsplash.com/photo-1574701606153-c5090aaf8c0e?w=400&h=400&fit=crop",
      hints: ["Gen Z icon", "Known for whisper singing", "'Bad Guy' singer"],
    },
    {
      id: "artist4",
      name: "DUA LIPA",
      imageUrl: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop",
      hints: ["British singer", "Future Nostalgia album", "Known for 'Levitating'"],
    },
    {
      id: "artist5",
      name: "BRUNO MARS",
      imageUrl: "https://images.unsplash.com/photo-1540479859555-17af45c78602?w=400&h=400&fit=crop",
      hints: ["Multi-instrumentalist", "Known for retro style", "'Uptown Funk' singer"],
    }
  ];

  // Helper function to check if user already won for a specific artist
  const checkIfUserAlreadyWon = (artistName, winnersArray) => {
    if (!userObj || !winnersArray || !artistName) return false;

    const userId = userObj.uid || userObj.id;
    return winnersArray.some(winner =>
      winner.userId === userId &&
      winner.artistName === artistName.toUpperCase()
    );
  };

  // Get a random artist from jukebox data
  const getRandomArtistFromJukebox = () => {
    // Filter jukebox for tracks with both artist and coverart, and only alphabetic artist names
    const validTracks = jukebox?.filter(track => {
      if (!track.artist || !track.coverart) return false;
      // Only allow artist names with letters and spaces (no numbers or special characters)
      const isAlphabeticOnly = /^[A-Za-z\s]+$/.test(track.artist);
      return isAlphabeticOnly;
    }) || [];

    // If no valid tracks, use demo data
    if (validTracks.length === 0) {
      return popArtists[Math.floor(Math.random() * popArtists.length)];
    }

    // Pick a random track
    const randomTrack = validTracks[Math.floor(Math.random() * validTracks.length)];

    // Generate hints for this artist
    const hints = [];
    if (randomTrack.genre) {
      hints.push(`Genre: ${randomTrack.genre}`);
    }
    if (randomTrack.track) {
      hints.push(`Song: "${randomTrack.track}"`);
    }
    // Find other tracks by same artist for additional hint
    const sameArtistTracks = validTracks.filter(t =>
      t.artist.toUpperCase() === randomTrack.artist.toUpperCase()
    );
    if (sameArtistTracks.length > 1) {
      hints.push(`${sameArtistTracks.length} songs in the jukebox`);
    }

    return {
      id: randomTrack.id || `artist-${Date.now()}`,
      name: randomTrack.artist.toUpperCase(),
      imageUrl: randomTrack.coverart,
      hints: hints.length > 0 ? hints : ["Popular artist", "In the jukebox", "Great music!"]
    };
  };

  // Set up hosted game listener
  useEffect(() => {
    if (gameMode === 'hosted' && clientObj?.clientId) {
      console.log('Setting up hosted mode listener...');
      console.log('Client ID:', clientObj.clientId);

      setWaitingForHost(true);
      const hostedGameRef = doc(db, "users", clientObj.clientId, "pixelpuzzler", "current");

      const unsubscribe = onSnapshot(hostedGameRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('Document data received:', data);

          // Use activeTrack directly if it exists
          if (data.activeTrack) {
            const selectedArtist = data.activeTrack;
            console.log('Using activeTrack:', selectedArtist);

            setHostedGameData(data);
            setIsConnected(true);
            setWaitingForHost(false);

            // Extract artist name and image - use whatever fields are there
            const artistName = selectedArtist.artist || selectedArtist.name || selectedArtist.artistName || "Unknown Artist";
            const imageUrl = selectedArtist.coverart || selectedArtist.image || selectedArtist.imageUrl || "";

            // Generate a unique ID for this track
            const newTrackId = selectedArtist.id || `${artistName}-${imageUrl}`;

            // Check if this is actually a NEW track (not the same one we already won)
            const isNewTrack = !currentArtist || currentArtist.id !== newTrackId;

            if (isNewTrack) {
              console.log('New track detected, resetting game');

              // Add detailed logging for debugging
              const userId = userObj?.uid || userObj?.id;
              console.log('Checking winners for:', {
                artistName: artistName.toUpperCase(),
                userId: userId,
                winners: data.winners || [],
                winnersCount: (data.winners || []).length
              });

              // Check if user already won this artist
              const userAlreadyWon = checkIfUserAlreadyWon(artistName, data.winners);
              console.log(`User ${userId} + Artist ${artistName.toUpperCase()} in winners: ${userAlreadyWon}`);

              // Set the new artist
              setCurrentArtist({
                id: newTrackId,
                name: artistName.toUpperCase(),
                imageUrl: imageUrl || "https://via.placeholder.com/400x400?text=Mystery+Artist",
                hints: selectedArtist.hints || ["Hosted game", "Mystery artist", "Good luck!"]
              });

              // Reset game for new artist
              setGuessedLetters([]);
              setWrongLetters([]);

              // Set blur based on whether user already won
              if (userAlreadyWon) {
                console.log('User already won this artist, showing clear image');
                setBlurLevel(0);
                setGameWon(true);
              } else {
                setBlurLevel(30);
                setGameWon(false);
              }

              setHints(0);
              setShowHint(false);
            } else if (gameWon) {
              console.log('Same track and already won, keeping win screen');
              // Keep the win screen showing
              // Make sure blur stays at 0 for won games
              setBlurLevel(0);
            } else {
              // Same track but not won yet - check if user previously won it
              const userAlreadyWon = checkIfUserAlreadyWon(artistName, data.winners);
              if (userAlreadyWon) {
                console.log('User previously won this artist, showing clear image');
                setBlurLevel(0);
                setGameWon(true);
              }
              // If not previously won, keep current game state as is
            }
          } else {
            console.log('No activeTrack found, waiting for host to set it');
            setWaitingForHost(true);
            setIsConnected(false);
          }
        } else {
          console.log('Document does not exist');
          setWaitingForHost(true);
          setIsConnected(false);
        }
      }, (error) => {
        console.error("Error listening to hosted game:", error);
        setIsConnected(false);
        setWaitingForHost(false);
      });

      return () => unsubscribe();
    }
  }, [gameMode, clientObj]);

  useEffect(() => {
    // Load random artist for solo mode
    if (gameMode === 'solo' && !currentArtist) {
      const randomArtist = getRandomArtistFromJukebox();
      setCurrentArtist(randomArtist);
    }
  }, [gameMode]); // Run when game mode changes

  useEffect(() => {
    if (!currentArtist) return;

    // Don't recalculate blur if game is already won
    if (gameWon) return;

    // Calculate blur based on correctly guessed letters
    const uniqueLetters = new Set(currentArtist.name.replace(/\s/g, ''));
    const correctlyGuessed = guessedLetters.filter(letter =>
      currentArtist.name.includes(letter)
    );

    // Reduce blur as more letters are guessed correctly
    const progress = correctlyGuessed.length / uniqueLetters.size;
    const newBlur = Math.max(0, 30 - (progress * 30));
    setBlurLevel(newBlur);

    // Check if game is won
    const nameLetters = currentArtist.name.replace(/\s/g, '').split('');
    const allGuessed = nameLetters.every(letter => guessedLetters.includes(letter));
    if (allGuessed && !gameWon) {
      handleWin();
    }
  }, [guessedLetters, currentArtist, gameWon]);

  const handleWin = async () => {
    setGameWon(true);
    setBlurLevel(0);

    const points = calculatePoints();
    setScore(prevScore => prevScore + points);

    // Save winner info to Firebase for hosted mode
    if (gameMode === 'hosted' && clientObj?.clientId && currentArtist && userObj) {
      try {
        const hostedGameRef = doc(db, "users", clientObj.clientId, "pixelpuzzler", "current");

        // First check if user already won THIS SPECIFIC ARTIST
        const docSnap = await getDoc(hostedGameRef);
        const currentData = docSnap.data() || {};
        const existingWinners = currentData.winners || [];

        const userId = auth.currentUser.uid;
        const userAlreadyWonThisArtist = existingWinners.some(winner =>
          winner.userId === userId && winner.artistName === currentArtist.name
        );

        if (!userAlreadyWonThisArtist) {
          // Add user to winners array
          const winnerData = {
            userId: userId,
            displayName: userObj.displayName || userObj.name || userObj.email || "Anonymous",
            photoURL: userObj.photoURL || userObj.avatar || userObj.profilePicture || "",
            timestamp: new Date().toISOString(), // Use ISO string for consistency in arrays
            artistName: currentArtist.name,
            points: points,
            hintsUsed: hints,
            wrongAttempts: wrongLetters.length,
            totalGuesses: guessedLetters.length
          };

          await updateDoc(hostedGameRef, {
            winners: arrayUnion(winnerData)
          });
          console.log("Winner added to winners array for artist:", currentArtist.name);
        } else {
          console.log("User already won this artist:", currentArtist.name);
        }
      } catch (error) {
        console.error("Error saving winner information:", error);
      }
    }

    // For solo mode, save to user's own document
    if (gameMode === 'solo' && userObj?.uid && currentArtist) {
      try {
        const gameRef = doc(db, "users", userObj.uid, "pixelpuzzler", "games");
        await setDoc(gameRef, {
          [`games.${currentArtist.id}`]: {
            artistName: currentArtist.name,
            guessedLetters: guessedLetters,
            wrongLetters: wrongLetters,
            hintsUsed: hints,
            points: points,
            timestamp: new Date().toISOString()
          },
          totalScore: score + points,
          lastUpdated: new Date().toISOString()
        }, { merge: true });
      } catch (error) {
        console.error("Error saving solo game:", error);
      }
    }
  };

  const calculatePoints = () => {
    const basePoints = 100;
    const wrongPenalty = wrongLetters.length * 5;
    const hintPenalty = hints * 10;
    const blurBonus = Math.floor(blurLevel * 2);

    return Math.max(10, basePoints - wrongPenalty - hintPenalty + blurBonus);
  };

  const handleMultipleLetterGuess = (input) => {
    const upperInput = input.toUpperCase().replace(/[^A-Z\s]/g, '');

    // Check if it's the full name (without spaces for comparison)
    if (upperInput.replace(/\s/g, '') === currentArtist.name.replace(/\s/g, '')) {
      // Instant win!
      const allLetters = currentArtist.name.replace(/\s/g, '').split('');
      const uniqueLetters = [...new Set(allLetters)];
      setGuessedLetters(uniqueLetters);
      setRecentCorrect(uniqueLetters);

      // Clear recent feedback after animation
      setTimeout(() => setRecentCorrect([]), 1000);
      return;
    }

    // Process individual letters
    const lettersToProcess = upperInput.replace(/\s/g, '').split('');
    const uniqueLetters = [...new Set(lettersToProcess)];

    const newCorrectLetters = [];
    const newWrongLetters = [];

    uniqueLetters.forEach(letter => {
      // Skip if already guessed
      if (!guessedLetters.includes(letter) && !wrongLetters.includes(letter)) {
        if (currentArtist.name.includes(letter)) {
          newCorrectLetters.push(letter);
        } else {
          newWrongLetters.push(letter);
        }
      }
    });

    // Update states with new letters
    if (newCorrectLetters.length > 0) {
      setGuessedLetters([...guessedLetters, ...newCorrectLetters]);
      setRecentCorrect(newCorrectLetters);

      // Clear recent correct feedback after animation
      setTimeout(() => setRecentCorrect([]), 2000);
    }

    if (newWrongLetters.length > 0) {
      setWrongLetters([...wrongLetters, ...newWrongLetters]);
      setRecentWrong(newWrongLetters);

      // Clear recent wrong feedback after animation
      setTimeout(() => setRecentWrong([]), 2000);
    }

    setCurrentInput('');
  };

  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (currentInput.trim() && /[a-zA-Z]/.test(currentInput)) {
      handleMultipleLetterGuess(currentInput);
    }
  };

  const getHint = () => {
    if (hints < currentArtist.hints.length) {
      setHints(hints + 1);
      setShowHint(true);

      // Also reveal a random unguessed letter
      const unguessedLetters = currentArtist.name
        .replace(/\s/g, '')
        .split('')
        .filter(letter => !guessedLetters.includes(letter));

      if (unguessedLetters.length > 0) {
        const randomLetter = unguessedLetters[Math.floor(Math.random() * unguessedLetters.length)];
        setGuessedLetters([...guessedLetters, randomLetter]);
      }
    }
  };

  const renderArtistName = () => {
    if (!currentArtist) return null;

    // Split by words first so each word appears on a separate line
    const words = currentArtist.name.split(' ');

    return (
      <div className="flex flex-col items-center gap-2">
        {words.map((word, wordIndex) => (
          <div key={`word-${wordIndex}`} className="flex justify-center">
            {word.split('').map((letter, letterIndex) => {
              const isGuessed = guessedLetters.includes(letter);
              return (
                <span
                  key={`${wordIndex}-${letterIndex}`}
                  className={`inline-block w-8 h-10 mx-1 border-b-2 text-center text-2xl font-bold
                    ${isGuessed
                      ? 'border-green-400 text-white'
                      : 'border-purple-400 text-transparent'
                    }`}
                >
                  {isGuessed ? letter : '_'}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const resetGame = () => {
    if (gameMode === 'solo') {
      const randomArtist = getRandomArtistFromJukebox();
      setCurrentArtist(randomArtist);
    }
    // In hosted mode, keep the same artist (controlled by host)
    setGuessedLetters([]);
    setWrongLetters([]);

    // Check if user already won this artist in hosted mode
    if (gameMode === 'hosted' && currentArtist && hostedGameData?.winners) {
      const userAlreadyWon = checkIfUserAlreadyWon(currentArtist.name, hostedGameData.winners);
      if (userAlreadyWon) {
        console.log('User already won this artist on reset, keeping clear image');
        setBlurLevel(0);
        setGameWon(true);
      } else {
        setBlurLevel(30);
        setGameWon(false);
      }
    } else {
      setBlurLevel(30);
      setGameWon(false);
    }

    setHints(0);
    setShowHint(false);
    setRecentCorrect([]);
    setRecentWrong([]);
  };

  const selectGameMode = (mode) => {
    setGameMode(mode);
    setModeSelected(true);
  };

  const exitGame = () => {
    setGameMode(null);
    setModeSelected(false);
    setCurrentArtist(null);
    setHostedGameData(null);
    setIsConnected(false);
    setWaitingForHost(false);
    resetGame();
  };

  // Mode Selection Screen
  if (!modeSelected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="relative max-w-md mx-auto min-h-screen">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

          <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Back"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <h1 className="text-2xl font-bold text-white tracking-wider">
                PIXEL PUZZLER
              </h1>

              <div className="w-10" />
            </div>

            {/* Mode Selection */}
            <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Choose Game Mode
                </h2>
                <p className="text-white/70">
                  Play solo or join a hosted game
                </p>
              </div>

              <div className="space-y-4 w-full max-w-sm">
                {/* Solo Mode Button */}
                <button
                  onClick={() => selectGameMode('solo')}
                  className="w-full p-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl hover:scale-105 transition-transform shadow-[0_0_30px_rgba(34,211,238,0.5)]"
                >
                  <div className="flex items-center justify-center gap-4">
                    <User size={32} className="text-white" />
                    <div className="text-left">
                      <h3 className="text-xl font-bold text-white">Solo Play</h3>
                      <p className="text-white/80 text-sm">Play with random artists</p>
                    </div>
                  </div>
                </button>

                {/* Hosted Mode Button */}
                <button
                  onClick={() => selectGameMode('hosted')}
                  className="w-full p-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl hover:scale-105 transition-transform shadow-[0_0_30px_rgba(168,85,247,0.5)]"
                >
                  <div className="flex items-center justify-center gap-4">
                    <Users size={32} className="text-white" />
                    <div className="text-left">
                      <h3 className="text-xl font-bold text-white">Hosted Game</h3>
                      <p className="text-white/80 text-sm">Join host's game</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Waiting for Host Screen (Hosted Mode)
  if (gameMode === 'hosted' && waitingForHost) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="relative max-w-md mx-auto min-h-screen">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

          <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
              <button
                onClick={exitGame}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Exit game"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <h1 className="text-2xl font-bold text-white tracking-wider">
                HOSTED MODE
              </h1>

              <div className="flex items-center gap-2">
                <WifiOff size={20} className="text-red-400" />
              </div>
            </div>

            {/* Waiting Message */}
            <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
                  <Users size={64} className="text-white" />
                </div>

                <h2 className="text-2xl font-bold text-white mb-4">
                  Waiting for Host
                </h2>
                <p className="text-white/70 mb-8">
                  The host will start a game soon...
                </p>

                <button
                  onClick={exitGame}
                  className="px-6 py-3 bg-red-500/20 border border-red-500/50 rounded-full text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Leave Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentArtist) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>

      <div className="relative max-w-md mx-auto min-h-screen">
        {/* Neon border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

        <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
            <button
              onClick={gameMode === 'hosted' ? exitGame : onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Back"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>

            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-wider">
                PIXEL PUZZLER
              </h1>
              <div className="flex items-center justify-center gap-2 text-sm">
                {gameMode === 'solo' ? (
                  <>
                    <User size={14} className="text-cyan-400" />
                    <span className="text-cyan-400">Solo Mode</span>
                  </>
                ) : (
                  <>
                    {isConnected ? (
                      <><Wifi size={14} className="text-green-400" />
                      <span className="text-green-400">Hosted</span></>
                    ) : (
                      <><WifiOff size={14} className="text-yellow-400" />
                      <span className="text-yellow-400">Connecting...</span></>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="px-3 py-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-full">
              <p className="text-lg font-bold text-white">{score}</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between text-white mb-2">
              <span className="text-sm">Clarity</span>
              <span className="text-sm">{Math.round(100 - (blurLevel * 3.33))}%</span>
            </div>
            <div className="h-2 bg-purple-800/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-500"
                style={{ width: `${100 - (blurLevel * 3.33)}%` }}
              />
            </div>
          </div>

          {/* Pixelated Image */}
          <div className="px-4 py-4">
            <div className="relative rounded-2xl overflow-hidden border-2 border-transparent bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 p-[2px]">
              <div className="relative bg-purple-950 rounded-2xl overflow-hidden">
                <img
                  src={currentArtist.imageUrl}
                  alt="Guess the artist"
                  className="w-full h-80 object-cover transition-all duration-1000"
                  style={{
                    filter: `blur(${blurLevel}px)`,
                    imageRendering: blurLevel > 15 ? 'pixelated' : 'auto',
                  }}
                />
                {gameWon && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="text-center">
                      <CheckCircle size={64} className="text-green-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-white">CORRECT!</p>
                      <p className="text-xl text-green-400">+{calculatePoints()} points</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Artist Name Display */}
          <div className="px-4 py-4">
            <div className="bg-purple-800/30 rounded-xl p-4 text-center">
              {renderArtistName()}
            </div>
          </div>

          {/* Letter Input */}
          {!gameWon && (
            <form onSubmit={handleInputSubmit} className="px-4 py-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value.toUpperCase())}
                  placeholder="Guess letters or the full name..."
                  className="flex-1 px-4 py-3 bg-black/70 border-2 border-purple-600 rounded-lg text-white focus:text-black placeholder-white/40 focus:outline-none focus:border-cyan-400 text-center text-xl font-bold tracking-wider"
                  aria-label="Letter or name input"
                />
                <button
                  type="submit"
                  disabled={!currentInput.trim() || !/[A-Z]/.test(currentInput)}
                  className="p-3 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-lg text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-transform"
                  aria-label="Submit guess"
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          )}

          {/* Recent Feedback */}
          {(recentCorrect.length > 0 || recentWrong.length > 0) && (
            <div className="px-4 py-2 animate-pulse">
              {recentCorrect.length > 0 && (
                <div className="mb-2">
                  <p className="text-green-400 text-sm font-bold mb-1">✓ Correct:</p>
                  <div className="flex flex-wrap gap-2">
                    {recentCorrect.map((letter, idx) => (
                      <span key={`${letter}-${idx}`} className="px-3 py-1 bg-green-500/30 border-2 border-green-400 rounded text-green-400 font-bold animate-bounce">
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {recentWrong.length > 0 && (
                <div>
                  <p className="text-red-400 text-sm font-bold mb-1">✗ Wrong:</p>
                  <div className="flex flex-wrap gap-2">
                    {recentWrong.map((letter, idx) => (
                      <span key={`${letter}-${idx}`} className="px-3 py-1 bg-red-500/30 border-2 border-red-400 rounded text-red-400 font-bold shake">
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* All Guessed Letters */}
          <div className="px-4 py-2">
            {wrongLetters.length > 0 && (
              <div className="mb-3">
                <p className="text-red-400/70 text-sm mb-1">Wrong Letters:</p>
                <div className="flex flex-wrap gap-2">
                  {wrongLetters.map(letter => (
                    <span key={letter} className={`px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-400 font-bold transition-all ${recentWrong.includes(letter) ? 'scale-110 border-2' : ''}`}>
                      {letter}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Hints Section */}
          {showHint && currentArtist.hints && (
            <div className="px-4 py-2">
              <div className="bg-yellow-500/10 border border-yellow-400/50 rounded-lg p-3">
                <p className="text-yellow-400 font-bold mb-1">Hints:</p>
                {currentArtist.hints.slice(0, hints).map((hint, index) => (
                  <p key={index} className="text-white/80 text-sm">• {hint}</p>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-4 py-4 space-y-2">
            {!gameWon ? (
              <button
                onClick={getHint}
                disabled={hints >= currentArtist.hints.length}
                className={`w-full py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2
                  ${hints >= currentArtist.hints.length
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:scale-105 shadow-[0_0_20px_rgba(251,191,36,0.5)]'
                  }`}
              >
                <Eye size={20} />
                {hints >= currentArtist.hints.length
                  ? 'No hints left'
                  : `Get Hint (${currentArtist.hints.length - hints} left)`}
              </button>
            ) : (
              gameMode === 'hosted' ? (
                <button
                  disabled
                  className="w-full py-3 rounded-full font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white opacity-75 cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Users size={20} />
                  Waiting for Host to Continue...
                </button>
              ) : (
                <button
                  onClick={resetGame}
                  className="w-full py-3 rounded-full font-bold bg-gradient-to-r from-green-400 to-green-500 text-white hover:scale-105 transition-all shadow-[0_0_20px_rgba(34,197,94,0.5)] flex items-center justify-center gap-2"
                >
                  <Sparkles size={20} />
                  Play Again
                </button>
              )
            )}
          </div>

          {/* Instructions */}
          <div className="px-4 pb-4">
            <div className="bg-purple-800/30 rounded-lg p-3 border border-purple-600/50">
              <p className="text-white/70 text-sm text-center font-medium mb-2">
                Guess the artist's name letter by letter or all at once!
              </p>
              <div className="flex flex-wrap justify-center gap-2 text-xs">
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">
                  ✓ Multiple letters
                </span>
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                  ✓ Full name
                </span>
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                  ✓ Instant win
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};